import React, { useState, useMemo, useEffect } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import axios from "axios";
import SiteFooter from "../components/SiteFooter";
import { CityAutocomplete } from "../components/CityAutocomplete";

const Global = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;700;900&display=swap');
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #FFFBF0; font-family: 'Pretendard', "Noto Sans KR", sans-serif; }
`;

const DancheongBar = styled.div`
  height: 6px; width: 100%;
  background: repeating-linear-gradient(90deg,#CC3333 0 20px,#C8901E 20px 40px,#4A7C6B 40px 60px,#2E6DA4 60px 80px,#C8901E 80px 100px);
  flex-shrink: 0;
`;

const floatHero = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-12px); }
`;
const fadeIn = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`;

/* ─── 공통 레이아웃 ─────────────────────────────────────────────────── */
const Wrap = styled.div`
  min-height: 100vh; display: flex; flex-direction: column; align-items: center;
  padding: 0 0 60px; background: #FFFBF0;
`;
/* 구형 Header — STEP 1에서만 사용 */
const Header = styled.div`text-align:center; margin: 0 0 20px; padding-top: 24px;`;
const Logo = styled.div`font-size:2rem; margin-bottom:6px;`;
const Title = styled.h1`font-family:'Noto Serif KR',serif; font-size:1.4rem; color:#2C1810; font-weight:900;`;
const Sub = styled.p`font-size:0.8rem; color:#9c7b5a; margin-top:5px;`;

/* ─── STEP 0 : 히어로 + 서비스카드 ──────────────────────────────────── */
const HookWrap = styled.div`width:100%; max-width:560px; padding: 0 14px;`;

/* 전체 씬 이미지 히어로 */
const HeroSceneWrap = styled.div`
  width: 100%; max-width: 560px;
  position: relative; overflow: hidden;
  margin: 0 auto;
`;
const HeroSceneImg = styled.img`
  width: 100%; display: block;
  object-fit: cover;
`;
const HeroSceneOverlay = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0; height: 120px;
  background: linear-gradient(to bottom, transparent, #FFFBF0);
`;
const HeroTitleBlock = styled.div`
  position: absolute; bottom: 16px; left: 0; right: 0;
  text-align: center; z-index: 2;
`;
const HeroTitle = styled.div`
  font-family: 'Noto Serif KR', serif;
  font-size: 1.7rem; font-weight: 900; color: #2C1810;
  text-shadow: 0 2px 8px rgba(255,251,240,0.8);
  margin-bottom: 4px;
`;
const HeroSub = styled.div`
  font-size: 0.8rem; color: #7A5C42; font-weight: 600;
  text-shadow: 0 1px 4px rgba(255,251,240,0.9);
`;

/* 섹션 헤더 */
const SectionLabel = styled.div`
  font-family: 'Noto Serif KR', serif;
  font-size: 0.95rem; font-weight: 900; color: #2C1810;
  padding: 20px 0 12px;
  display: flex; align-items: center; gap: 8px;
  &::after { content:''; flex:1; height:1.5px; background: linear-gradient(90deg,#C8901E44,transparent); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* 서비스 카드 — 이미지가 상단에 크게 */
const ServiceCardGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
  @media (max-width: 400px) { grid-template-columns: 1fr; }
`;
const ServiceCard = styled.button<{ $color: string; $idx: number }>`
  background: #fff;
  border: 1.5px solid ${p => p.$color}30;
  border-radius: 20px;
  padding: 0;
  cursor: pointer; text-align: left;
  transition: transform 0.2s, box-shadow 0.2s;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: ${p => p.$idx * 0.07}s;
  overflow: hidden; font-family: inherit;
  box-shadow: 0 4px 18px rgba(0,0,0,0.07);
  &:hover { transform: translateY(-6px); box-shadow: 0 14px 36px ${p => p.$color}28; border-color: ${p => p.$color}60; }
  &:active { transform: translateY(-2px); }
`;
const SvcCardImg = styled.div<{ $src: string }>`
  width: 100%; height: 130px;
  background-image: url(${p => p.$src});
  background-size: cover; background-position: center top;
  border-radius: 18px 18px 0 0;
  position: relative;
  &::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 40px;
    background: linear-gradient(to bottom, transparent, #fff);
  }
`;
const SvcCardBody = styled.div<{ $color: string }>`
  padding: 10px 14px 14px;
  border-top: 2.5px solid ${p => p.$color}22;
`;
const SvcIconImg = styled.img`width:32px; height:32px; object-fit:contain; filter:drop-shadow(0 2px 6px rgba(0,0,0,0.15)); margin-bottom:4px;`;
const SvcIcon = styled.div`font-size: 1.3rem; margin-bottom: 4px; line-height: 1;`;
const SvcHook = styled.div<{ $color: string }>`
  font-size: 12.5px; font-weight: 800; color: ${p => p.$color};
  line-height: 1.45; margin-bottom: 4px;
`;
const SvcSub = styled.div`font-size: 10px; color: #7a6654; line-height: 1.4; margin-bottom: 10px;`;
const SvcBtn = styled.div<{ $color: string }>`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 700; color: #fff;
  background: ${p => p.$color}; border-radius: 20px; padding: 5px 12px;
`;

/* ─── STEP 1: 입력 폼 페이지 ────────────────────────────────────────── */
const FormWrap = styled.div`
  width: 100%;
  max-width: 520px;
`;
const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #9c7b5a;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 0 16px;
  font-family: inherit;
  &:hover { color: #7a6654; }
`;

const ServiceHeaderBand = styled.div<{ $color: string }>`
  background: linear-gradient(135deg, #FFFBF0, ${p => p.$color}12);
  border-bottom: 2px solid ${p => p.$color}30;
  padding: 14px 18px 12px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;
const ServiceHeaderTextBlock = styled.div`flex: 1;`;
const ServiceHeaderImg = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 50%;
  background: #FFF3DC;
  border: 2px solid #C8901E44;
  flex-shrink: 0;
`;
const ServiceHeaderIcon = styled.span`font-size: 1.3rem; margin-right: 8px;`;
const ServiceHeaderHook = styled.div<{ $color: string }>`
  font-family: 'Noto Serif KR', serif;
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.$color};
  line-height: 1.45;
`;
const ServiceHeaderSub = styled.div`font-size: 11px; color: #9c7b5a; margin-top: 3px;`;

const Card = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 0 0 28px;
  border: 1.5px solid #E8D5B5;
  box-shadow: 0 8px 36px rgba(200,144,30,0.12), 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
`;

/* ─── 하위 옵션 칩 ──────────────────────────────────────────────────── */
const SubOptWrap = styled.div<{ $visible: boolean }>`
  overflow: hidden;
  max-height: ${p => p.$visible ? "150px" : "0"};
  transition: max-height 0.25s ease;
  background: #F8F9FA;
  border-bottom: 1px solid #E9ECEF;
`;
const SubOptInner = styled.div`padding: 12px 16px;`;
const SubOptLabel = styled.div`font-size: 11px; font-weight: 700; color: #868E96; margin-bottom: 8px; letter-spacing: 0.06em;`;
const ChipRow = styled.div`display: flex; gap: 6px; flex-wrap: wrap;`;
const Chip = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 16px;
  border: 1.5px solid ${p => p.$active ? "#c09a6b" : "#DEE2E6"};
  background: ${p => p.$active ? "#FFF3E6" : "#fff"};
  color: ${p => p.$active ? "#8b5e3c" : "#495057"};
  font-size: 12.5px;
  font-weight: ${p => p.$active ? 700 : 400};
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
  &:hover { border-color: #c09a6b; }
`;

/* ─── 폼 ──────────────────────────────────────────────────────────── */
const FormBody = styled.form`padding: 20px 20px 0;`;
const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9c7b5a;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 18px 0 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  &:first-child { margin-top: 0; }
`;
const FieldRow = styled.div`display: flex; gap: 8px; margin-bottom: 10px;`;
const Field = styled.div`flex: 1; display: flex; flex-direction: column; gap: 4px;`;
const Label = styled.label`font-size: 12px; color: #5a4a3a; font-weight: 600;`;
const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e0d5c8;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #3d2b1f;
  background: #fdfaf7;
  font-family: inherit;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: #c09a6b; }
`;
const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e0d5c8;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #3d2b1f;
  background: #fdfaf7;
  font-family: inherit;
  &:focus { outline: none; border-color: #c09a6b; }
`;

/* ─── 파트너 폼 ─────────────────────────────────────────────────────── */
const CompatNoticeBand = styled.div`
  margin: 0;
  padding: 12px 20px;
  background: #FFF5F5;
  border-bottom: 1px solid #FFE3E3;
`;
const CompatNoticeTitle = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #E03131;
  margin-bottom: 4px;
`;
const CompatNoticeText = styled.div`
  font-size: 11.5px;
  color: #555;
  line-height: 1.6;
`;
const PartnerWrap = styled.div<{ $visible: boolean }>`
  overflow: hidden;
  max-height: ${p => p.$visible ? "700px" : "0"};
  transition: max-height 0.3s ease;
`;
const PartnerDivider = styled.div`
  margin: 0 -20px 0;
  padding: 14px 20px 0;
  background: linear-gradient(180deg, #F8F9FA 0%, #fff 100%);
  border-top: 2px dashed #E0D5C8;
`;
const PartnerTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #1A1A2E;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;
const PartnerSub = styled.div`
  font-size: 11px;
  color: #868E96;
  margin-bottom: 14px;
`;

/* ─── 동의 / 제출 ───────────────────────────────────────────────────── */
const ConsentBox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.8rem;
  color: #5a4a3a;
  line-height: 1.5;
  margin: 20px 0 0;
  cursor: pointer;
  input { margin-top: 2px; accent-color: #c09a6b; width: 16px; height: 16px; flex-shrink: 0; }
`;
const SubmitBtn = styled.button<{ $disabled?: boolean }>`
  width: calc(100% - 40px);
  margin: 18px 20px 0;
  padding: 15px;
  background: ${p => p.$disabled ? "#ccc" : "linear-gradient(135deg,#c09a6b,#8b5e3c)"};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: ${p => p.$disabled ? "not-allowed" : "pointer"};
  font-family: inherit;
  transition: opacity 0.2s;
  &:hover { opacity: ${p => p.$disabled ? 1 : 0.9}; }
`;
const ErrorMsg = styled.p`
  color: #c0392b;
  font-size: 0.82rem;
  margin: 10px 20px 0;
  text-align: center;
`;


/* ─── 로딩 오버레이 ──────────────────────────────────────────────── */
const floatAnim   = keyframes`0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)}`;
const dotBounce   = keyframes`0%,80%,100%{opacity:0.2;transform:scale(0.8)} 40%{opacity:1;transform:scale(1.2)}`;
const glowPulse   = keyframes`0%,100%{box-shadow:0 0 24px #C8901E55,0 6px 24px #C8901E44} 50%{box-shadow:0 0 48px #C8901Eaa,0 8px 32px #C8901E88}`;
const lockPulse   = keyframes`0%,100%{transform:scale(1)} 50%{transform:scale(1.12)}`;
const revealText  = keyframes`from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)}`;
const sparkle     = keyframes`0%{opacity:0;transform:scale(0) rotate(0deg)} 50%{opacity:1;transform:scale(1) rotate(180deg)} 100%{opacity:0;transform:scale(0) rotate(360deg)}`;
const shimmerBg   = keyframes`0%{background-position:200% center} 100%{background-position:-200% center}`;

const LoadingOverlay = styled.div`
  position: fixed; inset: 0; z-index: 999;
  background: rgba(255,248,240,0.97);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 0;
`;
const LoadingChar = styled.img`
  width: 120px; height: 120px; object-fit: contain;
  border-radius: 50%;
  animation: ${floatAnim} 2s ease-in-out infinite;
  filter: drop-shadow(0 8px 24px rgba(200,144,30,0.3));
  margin-bottom: 28px;
`;
const LoadingTitle = styled.div`
  font-family: 'Noto Serif KR', serif;
  font-size: 1.1rem; font-weight: 900; color: #2C1810;
  margin-bottom: 10px; letter-spacing: -0.3px;
`;
const LoadingDots = styled.div`
  display: flex; gap: 7px; margin-bottom: 28px;
  span {
    width: 8px; height: 8px; border-radius: 50%;
    background: #C8901E; display: inline-block;
    animation: ${dotBounce} 1.2s ease-in-out infinite;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;
const LoadingProgress = styled.div`
  width: 200px; height: 3px; background: #F0E8D8; border-radius: 999px; overflow: hidden;
  &::after {
    content: ''; display: block; height: 100%;
    background: linear-gradient(90deg, #C8901E, #E8A530, #C8901E);
    background-size: 200% auto;
    border-radius: 999px;
    animation: ${shimmerBg} 1.5s linear infinite;
  }
`;

/* ─── 미리보기 화면 ──────────────────────────────────────────────── */
const PreviewPage = styled.div`
  width: 100%; min-height: 100vh;
  background: #FFFBF0;
  display: flex; flex-direction: column; align-items: center;
  padding: 0 0 60px;
`;
const PreviewTopBar = styled.div`
  height: 6px; width: 100%;
  background: repeating-linear-gradient(90deg,#CC3333 0 20px,#C8901E 20px 40px,#4A7C6B 40px 60px,#2E6DA4 60px 80px,#C8901E 80px 100px);
`;
const PreviewHero = styled.div`
  width: 100%; max-width: 520px;
  padding: 24px 20px 0;
  display: flex; align-items: center; gap: 14px;
  animation: ${revealText} 0.5s ease both;
`;
const PreviewCharImg = styled.img`
  width: 60px; height: 60px;
  object-fit: contain;
  filter: drop-shadow(0 4px 10px rgba(200,144,30,0.3));
  flex-shrink: 0;
`;
const PreviewBrandBox = styled.div`flex: 1;`;
const PreviewBrand = styled.div`
  font-family: 'Noto Serif KR', serif;
  font-size: 1.1rem; font-weight: 900; color: #2C1810;
`;
const PreviewBrandSub = styled.div`font-size: 0.72rem; color: #9c7b5a; margin-top: 2px;`;

const PreviewBadge = styled.div<{ $color: string }>`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 14px;
  background: ${p => p.$color}18;
  border: 1.5px solid ${p => p.$color}55;
  border-radius: 20px;
  font-size: 0.75rem; font-weight: 800;
  color: ${p => p.$color};
  margin: 20px 20px 0;
  animation: ${revealText} 0.5s 0.1s ease both; opacity: 0;
  animation-fill-mode: forwards;
`;

const PreviewCard = styled.div`
  width: calc(100% - 32px); max-width: 488px;
  margin: 14px 16px 0;
  background: #fff;
  border-radius: 22px;
  border: 2px solid #E8D5B5;
  box-shadow: 0 8px 40px rgba(200,144,30,0.12);
  overflow: hidden;
  animation: ${revealText} 0.5s 0.2s ease both; opacity: 0;
  animation-fill-mode: forwards;
`;
const PreviewCardTop = styled.div`
  height: 5px;
  background: repeating-linear-gradient(90deg,#CC3333 0 16px,#C8901E 16px 32px,#4A7C6B 32px 48px,#2E6DA4 48px 64px);
`;
const PreviewCardInner = styled.div`padding: 22px 20px 0;`;
const PreviewAnalysisTitle = styled.div`
  font-family: 'Noto Serif KR', serif;
  font-size: 1rem; font-weight: 900; color: #2C1810;
  margin-bottom: 14px; display: flex; align-items: center; gap: 8px;
`;
const PreviewTextBoxOuter = styled.div`
  position: relative;
  height: 420px;
  overflow: hidden;
`;
const PreviewScrollArea = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 0 4px 80px;
  scrollbar-width: thin;
  scrollbar-color: #E8D5B5 transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #E8D5B5; border-radius: 4px; }
`;
const PreviewTextBox = styled.div`
  font-size: 0.92rem; color: #2C1810; line-height: 2.2;
  text-align: left;
  white-space: pre-line;
  word-break: keep-all;
  animation: ${revealText} 0.7s 0.4s ease both; opacity: 0;
  animation-fill-mode: forwards;
`;
const PreviewFade = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0; height: 140px;
  background: linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.8) 50%, #fff 100%);
  pointer-events: none;
`;
const PreviewLockSection = styled.div`
  padding: 8px 20px 20px;
  text-align: center;
  border-top: 1px solid #F0E8D8;
  margin-top: 8px;
`;
const LockIcon = styled.div`
  font-size: 2rem; margin-bottom: 6px;
  animation: ${lockPulse} 2s ease-in-out infinite;
  display: inline-block;
`;
const LockTitle = styled.div`
  font-family: 'Noto Serif KR', serif;
  font-size: 0.95rem; font-weight: 900; color: #2C1810;
  margin-bottom: 4px;
`;
const LockSub = styled.div`font-size: 0.75rem; color: #9c7b5a; margin-bottom: 14px; line-height: 1.6;`;

const UnlockList = styled.div`
  background: #FFF8EE; border-radius: 12px;
  padding: 12px 14px; margin-bottom: 18px; text-align: left;
`;
const UnlockItem = styled.div<{ $delay: number }>`
  font-size: 0.78rem; color: #5A3E2E;
  padding: 4px 0; display: flex; align-items: flex-start; gap: 7px;
  animation: ${revealText} 0.4s ${p => p.$delay}s ease both; opacity: 0;
  animation-fill-mode: forwards;
  &::before { content: '✓'; color: #C8901E; font-weight: 900; flex-shrink: 0; }
`;
const PayBtn = styled.button`
  width: 100%; padding: 16px;
  background: linear-gradient(135deg, #C8901E, #E8A530, #C8901E);
  background-size: 200% auto;
  color: #fff; border: none; border-radius: 50px;
  font-size: 1.05rem; font-weight: 900;
  cursor: pointer; font-family: inherit;
  animation: ${glowPulse} 2s ease-in-out infinite;
  transition: transform 0.15s;
  letter-spacing: -0.3px;
  &:active { transform: scale(0.97); }
`;
const PayInfo = styled.div`
  font-size: 0.72rem; color: #9c7b5a; text-align: center;
  margin-top: 10px; margin-bottom: 4px; line-height: 1.8;
`;
const SparkleWrap = styled.div`
  position: absolute; inset: 0; pointer-events: none; overflow: hidden;
`;
const SparkleItem = styled.div<{ $x: number; $y: number; $delay: number; $size: number }>`
  position: absolute;
  left: ${p => p.$x}%; top: ${p => p.$y}%;
  font-size: ${p => p.$size}rem;
  animation: ${sparkle} 3s ${p => p.$delay}s ease-in-out infinite;
`;

/* ─── 완료 카드 (레거시, 무료발송 모드용) ────────────────────────── */
const SuccessCard = styled.div`
  width: 100%; max-width: 520px;
  background: #fff; border: 1.5px solid #e0d5c8;
  border-radius: 20px; padding: 48px 24px;
  box-shadow: 0 4px 24px rgba(100,70,40,0.1); text-align: center;
`;
const SuccessIcon = styled.div`font-size: 3rem; margin-bottom: 16px;`;
const SuccessTitle = styled.h2`font-size: 1.3rem; color: #3d2b1f; margin-bottom: 10px;`;
const SuccessDesc = styled.p`font-size: 0.88rem; color: #5a4a3a; line-height: 1.7;`;
const ServiceBadge = styled.span`
  display: inline-block; padding: 4px 12px;
  background: #FFF3E6; border: 1px solid #e0d5c8;
  border-radius: 20px; font-size: 12px; font-weight: 700; color: #8b5e3c; margin-bottom: 16px;
`;
const ResetBtn = styled.button`
  margin-top: 16px; padding: 11px 28px;
  background: transparent; color: #9c7b5a;
  border: 1.5px solid #e0d5c8; border-radius: 50px;
  font-size: 0.85rem; font-weight: 700; cursor: pointer; font-family: inherit;
  &:hover { background: #f9f4ef; }
`;

/* ─── 상수 ───────────────────────────────────────────────────────────── */
const API = process.env.REACT_APP_API_URL || "";
const BIRTH_TYPES = [
  { value: "solar", label: "양력" },
  { value: "lunar", label: "음력" },
  { value: "lunar_leap", label: "음력 윤달" },
];
const CITY_GROUPS = [
  { label: "🇰🇷 국내", cities: ["서울","부산","대구","인천","광주","대전","울산","제주","수원","성남","용인","청주","전주","강릉","포항","창원","고양","춘천","목포","세종"] },
  { label: "🌏 동아시아", cities: ["도쿄","오사카","베이징","상하이","홍콩","타이베이","마카오","울란바토르"] },
  { label: "🌏 동남아시아", cities: ["방콕","싱가포르","쿠알라룸푸르","자카르타","마닐라","하노이","호치민","양곤","프놈펜","비엔티안"] },
  { label: "🌏 남아시아·중앙아시아", cities: ["뉴델리","뭄바이","다카","카라치","이슬라마바드","콜롬보","카트만두","타슈켄트","알마티","아스타나"] },
  { label: "🌍 중동", cities: ["두바이","아부다비","도하","리야드","테헤란","이스탄불","앙카라","바그다드","베이루트","암만","예루살렘","쿠웨이트시티"] },
  { label: "🌍 서유럽", cities: ["런던","파리","베를린","마드리드","로마","암스테르담","브뤼셀","취리히","비엔나","코펜하겐","스톡홀름","오슬로","헬싱키","리스본","더블린"] },
  { label: "🌍 동유럽·러시아", cities: ["모스크바","상트페테르부르크","키이우","바르샤바","프라하","부다페스트","부쿠레슈티","아테네","소피아","베오그라드","민스크","탈린","리가","빌뉴스"] },
  { label: "🌎 북미", cities: ["뉴욕","워싱턴DC","로스앤젤레스","시카고","샌프란시스코","시애틀","토론토","밴쿠버","오타와","멕시코시티"] },
  { label: "🌎 중남미·카리브", cities: ["보고타","상파울루","부에노스아이레스","리마","산티아고","카라카스","브라질리아","몬테비데오","아바나","파나마시티"] },
  { label: "🌏 오세아니아", cities: ["시드니","멜버른","캔버라","브리즈번","오클랜드","웰링턴"] },
  { label: "🌍 아프리카", cities: ["카이로","나이로비","요하네스버그","라고스","아디스아바바","카사블랑카","다카르","아크라","알제","튀니스"] },
];

const SERVICE_TYPES = [
  { key: "종합운세",    color: "#1971C2", icon: "✨", img: "/woo_04.png", iconImg: "/woo_y_3.png", hook: "알고 나면 아무것도 아닌데, 모르면 평생 숙제입니다.", sub: "타고난 명운과 인생 전체 흐름 정밀 분석" },
  { key: "신년운세",    color: "#7048E8", icon: "🌅", img: "/woo_03.png", iconImg: "/woo_y_5.png", hook: "새해, 운의 흐름을 먼저 읽는 자가 앞서 갑니다.", sub: "올해 월별 운세 전체 흐름 분석" },
  { key: "연애운",      color: "#E64980", icon: "❤️", img: "/woo_08.png", iconImg: "/woo_y_1.png", hook: "인연은 준비된 사람에게 찾아옵니다.", sub: "인연을 만나는 시기와 상대방 특징 분석" },
  { key: "연애궁합",    color: "#FF6B9D", icon: "💑", img: "/woo_02.png", iconImg: "/woo_y_2.png", hook: "끌리는 이유가 있습니다. 사주로 증명됩니다.", sub: "속궁합·성격궁합·첫만남 년도·한난조습 분석" },
  { key: "심층궁합",    color: "#0CA678", icon: "💕", img: "/woo_02.png", iconImg: "/woo_y_2.png", hook: "결혼까지 가는 사람인지, 사주가 알고 있습니다.", sub: "결혼운·자녀운·이별위험시기 정밀 분석" },
  { key: "직업운",      color: "#F59F00", icon: "🎯", img: "/woo_05.png", iconImg: "/woo_y_6.png", hook: "지금 이 선택, 운이 받쳐주는 타이밍인가요?", sub: "커리어 전환·승진의 최적 시기 분석" },
  { key: "사업(재물운)", color: "#2F9E44", icon: "📈", img: "/woo_05.png", iconImg: "/woo_y_3.png", hook: "열심히 사는데 제자리라면, '운의 파도'를 체크하세요.", sub: "내 재물이 터지는 구체적인 시기 확인" },
  { key: "건강운",      color: "#20C997", icon: "🌿", img: "/woo_07.png", iconImg: "/woo_y_5.png", hook: "몸이 보내는 신호, 사주가 먼저 알고 있습니다.", sub: "취약 부위·사고수·수술수 시기 분석" },
];

/* 서비스별 결제 금액 */
const SVC_PRICE: Record<string, number> = {
  "종합운세": 10000, "신년운세": 5000,
  "연애운": 5000,    "연애궁합": 10000,
  "심층궁합": 10000, "직업운": 5000,
  "사업(재물운)": 5000, "건강운": 5000,
};

/* 결제 후 잠금 해제 콘텐츠 설명 */
const SVC_UNLOCK: Record<string, string[]> = {
  "종합운세":    ["타고난 격국·용신 분석", "결혼·직업·건강 구체적 년도 예측", "대운별 인생 흐름 서술"],
  "신년운세":    ["1~12월 월별 운세", "월별 용신·기신 날짜", "주의 시기 상세 설명"],
  "연애운":      ["인연 만날 가능성 높은 날짜", "상대방 외모·성격·직업 특징", "이별수·주의 시기"],
  "연애궁합":    ["첫 만남 년도 예측", "한난조습 에너지 분석", "속궁합·성격궁합·가치관 상세"],
  "심층궁합":    ["성격 궁합·속궁합", "결혼운·자녀운", "이별 위험 시기 전체"],
  "직업운":      ["합격·승진 최적일 TOP 3", "이직 황금 타이밍", "직종 추천"],
  "사업(재물운)": ["계약·시작 최적일 TOP 3", "재물 터지는 시기", "업종 추천"],
  "건강운":      ["취약 부위 상세 분석", "사고수·수술수 위험 시기", "건강 관리 최적 시기"],
};

const COMPAT_SERVICES = ["심층궁합", "연애궁합"];

const ADULT_OPTIONS = ["연애운", "직업운", "사업(재물운)", "건강운"];
const MINOR_OPTIONS = ["학업운", "가족/교우관계", "연애운", "건강운"];

function parseTime(val: string) {
  const s = val.replace(/\s/g, "");
  if (!s) return { hour: 99, minute: 0 };
  let h: number, m: number;
  if (s.includes(":")) {
    const parts = s.split(":");
    h = parseInt(parts[0]); m = parseInt(parts[1] || "0");
  } else if (/^\d{3,4}$/.test(s)) {
    const p = s.padStart(4, "0");
    h = parseInt(p.slice(0, 2)); m = parseInt(p.slice(2));
  } else {
    h = parseInt(s); m = 0;
  }
  return {
    hour: (!isNaN(h) && h >= 0 && h <= 23) ? h : 99,
    minute: (!isNaN(m) && m >= 0 && m <= 59) ? m : 0,
  };
}

function parseBirthDate(val: string) {
  const sep = val.includes("/") ? "/" : val.includes(".") ? "." : val.includes("-") ? "-" : null;
  if (sep) { const [y,m,d] = val.split(sep).map(Number); return { year:y||0,month:m||0,day:d||0 }; }
  if (val.length === 8) return { year:Number(val.slice(0,4)),month:Number(val.slice(4,6)),day:Number(val.slice(6,8)) };
  return { year:0,month:0,day:0 };
}

interface PartnerForm {
  name: string; gender: string; birth_type: string;
  birth_date: string;
  birth_time: string;
  birth_time_unknown: boolean;
  city: string;
}
const defaultPartner: PartnerForm = {
  name: "", gender: "female", birth_type: "solar",
  birth_date: "",
  birth_time: "",
  birth_time_unknown: false,
  city: "서울",
};

/* ─── 시주 추정 설문 모달 styled ────────────────────────────────────── */
const SurveyOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.55); z-index: 999;
  display: flex; align-items: center; justify-content: center; padding: 16px;
`;
const SurveyModal = styled.div`
  background: #fff; border-radius: 18px; width: 100%; max-width: 520px;
  max-height: 88vh; overflow-y: auto; padding: 26px 22px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.22);
`;
const SurveyQBlock = styled.div` margin-bottom: 18px; `;
const SurveyQText = styled.div`
  font-size: 0.83rem; font-weight: 700; color: #3d2b1f; margin-bottom: 7px;
`;
const SurveyChoiceLabel = styled.label<{ $selected: boolean }>`
  display: flex; align-items: flex-start; gap: 8px;
  padding: 8px 12px; border-radius: 8px; cursor: pointer; font-size: 0.8rem;
  margin-bottom: 5px;
  background: ${p => p.$selected ? "#FFF3E0" : "#F8F4F0"};
  border: 1.5px solid ${p => p.$selected ? "#c09a6b" : "#e8dfd5"};
  color: ${p => p.$selected ? "#7a4f2d" : "#5a4535"};
  transition: all 0.15s;
  &:hover { background: #FFF0E3; border-color: #c09a6b; }
`;
const SurveyResultItem = styled.div<{ $rank: number }>`
  display: flex; align-items: center; gap: 10px;
  padding: 11px 14px; border-radius: 10px; margin-bottom: 7px;
  background: ${p => p.$rank === 1 ? "#FFF9DB" : p.$rank === 2 ? "#F3F0FF" : "#F0F8FF"};
  border: 1.5px solid ${p => p.$rank === 1 ? "#F59F00" : p.$rank === 2 ? "#9775FA" : "#74C0FC"};
`;

export default function ClientPage() {
  const [step,        setStep]        = useState<0 | 1>(0);
  const [serviceType, setServiceType] = useState("종합운세");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const svc = params.get("service");
    if (svc && SERVICE_TYPES.some(s => s.key === svc)) {
      setServiceType(svc);
      setStep(1);
    }
  }, []);
  const [options,     setOptions]     = useState<string[]>([]);
  const [form,        setForm]        = useState({
    name: "", email: "", gender: "female",
    birth_date: "",
    birth_time: "",
    birth_time_unknown: false,
    birth_type: "solar", city: "서울", consent: false,
  });
  const [partner,  setPartner]  = useState<PartnerForm>(defaultPartner);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  /* ─── 시주 추정 설문 state ─── */
  const [surveying,     setSurveying]     = useState<"self"|"partner"|null>(null);
  const [surveyQs,      setSurveyQs]      = useState<{id:number;question:string;choices:string[]}[]>([]);
  const [surveyAns,     setSurveyAns]     = useState<number[]>(Array(10).fill(-1));
  const [surveyResults, setSurveyResults] = useState<any[]>([]);
  // 제출 후 미리보기 상태
  const [preview, setPreview] = useState<{
    consultationId: number;
    text: string;
    serviceType: string;
    paymentEnabled: boolean;
    emailSent: boolean;
  } | null>(null);

  const age = useMemo(() => {
    const { year } = parseBirthDate(form.birth_date);
    if (!year) return null;
    return new Date().getFullYear() - year;
  }, [form.birth_date]);
  const isMinor = age !== null && age < 20;
  const currentOptions = isMinor ? MINOR_OPTIONS : ADULT_OPTIONS;
  const isCompat = COMPAT_SERVICES.includes(serviceType);

  const openSurvey = async (target: "self" | "partner") => {
    if (surveyQs.length === 0) {
      try {
        const r = await axios.get(`${process.env.REACT_APP_API_URL || ""}/api/v1/saju/hour-survey`);
        setSurveyQs(r.data.questions);
      } catch { return; }
    }
    setSurveyAns(Array(10).fill(-1));
    setSurveyResults([]);
    setSurveying(target);
  };
  const closeSurvey = () => setSurveying(null);
  const submitSurvey = async () => {
    if (surveyAns.some(a => a < 0)) return;
    try {
      const r = await axios.post(`${process.env.REACT_APP_API_URL || ""}/api/v1/saju/estimate-hour`, { answers: surveyAns });
      setSurveyResults(r.data.recommendations);
    } catch {}
  };
  const applySurveyResult = (birthHour: number) => {
    const timeStr = String(birthHour).padStart(2,"0") + ":00";
    if (surveying === "self") {
      setForm(f => ({ ...f, birth_time: timeStr, birth_time_unknown: false }));
    } else if (surveying === "partner") {
      setPartner(p => ({ ...p, birth_time: timeStr, birth_time_unknown: false }));
    }
    setSurveying(null);
  };

  const set  = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }));
  const setP = (key: keyof PartnerForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setPartner(p => ({ ...p, [key]: e.target.value }));
  const toggleOption = (opt: string) =>
    setOptions(prev => prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]);

  const selectService = (svc: string) => {
    setServiceType(svc);
    setOptions([]);
    setStep(1);           // 서비스 선택 → 입력 폼으로 이동
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.consent) { setError("개인정보 활용에 동의해주세요."); return; }
    if (!form.email || !form.email.includes("@")) {
      setError("결과 리포트 수신을 위해 이메일 주소를 입력해주세요."); return;
    }
    if (!form.name || !form.birth_date) {
      setError("필수 항목(이름, 생년월일)을 모두 입력해주세요."); return;
    }
    const { year: by, month: bm, day: bd } = parseBirthDate(form.birth_date);
    if (!by || !bm || !bd) {
      setError("생년월일을 올바르게 입력해주세요. 예: 1990/05/15"); return;
    }
    if (isCompat) {
      const pd = parseBirthDate(partner.birth_date);
      if (!partner.name || !pd.year || !pd.month || !pd.day) {
        setError("상대방 정보를 모두 입력해주세요."); return;
      }
    }
    setLoading(true);
    setError("");
    try {
      const pd = parseBirthDate(partner.birth_date);
      const res = await axios.post(`${API}/api/v1/client/submit`, {
        name: form.name,
        email: form.email || "",
        gender: form.gender,
        birth_year: by,
        birth_month: bm,
        birth_day: bd,
        birth_hour: form.birth_time_unknown ? 99 : parseTime(form.birth_time).hour,
        birth_minute: form.birth_time_unknown ? 0 : parseTime(form.birth_time).minute,
        birth_type: form.birth_type,
        city: form.city,
        consent: true,
        service_type: serviceType,
        service_options: options,
        partner_info: isCompat ? {
          name: partner.name,
          gender: partner.gender,
          birth_type: partner.birth_type,
          birth_year: pd.year,
          birth_month: pd.month,
          birth_day: pd.day,
          birth_hour: partner.birth_time_unknown ? 99 : parseTime(partner.birth_time).hour,
          birth_minute: partner.birth_time_unknown ? 0 : parseTime(partner.birth_time).minute,
          city: partner.city,
          compatibility_type: serviceType === "심층궁합" ? "deep" : "casual",
        } : null,
      });
      setPreview({
        consultationId: res.data.consultation_id,
        text: res.data.preview_text || "분석이 완료되었습니다.",
        serviceType: res.data.service_type || serviceType,
        paymentEnabled: res.data.payment_enabled ?? true,
        emailSent: res.data.email_sent ?? false,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || "제출 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 포트원 결제 핸들러
  const handlePayment = () => {
    if (!preview) return;
    const IMP = (window as any).IMP;
    if (!IMP) { alert("결제 모듈 로딩 중입니다. 잠시 후 다시 시도해주세요."); return; }
    IMP.init(process.env.REACT_APP_IMP_CODE || "imp00000000");
    IMP.request_pay({
      pg: "html5_inicis",
      pay_method: "card",
      merchant_uid: `saju_${preview.consultationId}_${Date.now()}`,
      name: `우연 사주 — ${preview.serviceType} 전체 리포트`,
      amount: SVC_PRICE[preview.serviceType] || 9900,
      buyer_name: form.name,
      buyer_email: form.email || "",
    }, async (rsp: any) => {
      if (rsp.success) {
        try {
          await axios.post(`${API}/api/v1/client/payment-verify`, {
            consultation_id: preview.consultationId,
            imp_uid: rsp.imp_uid,
            merchant_uid: rsp.merchant_uid,
          });
          window.location.href = `/report/${preview.consultationId}`;
        } catch {
          alert("결제는 완료되었으나 처리 중 오류가 발생했습니다. 관리자에게 문의해주세요.");
        }
      } else {
        alert(`결제 실패: ${rsp.error_msg || "다시 시도해주세요."}`);
      }
    });
  };

  /* ─── 로딩 화면 ─── */
  if (loading) {
    return (
      <>
        <Global />
        <LoadingOverlay>
          <LoadingChar src="/woo_03.png" alt="분석 중" />
          <LoadingTitle>사주를 분석하는 중입니다</LoadingTitle>
          <LoadingDots>
            <span /><span /><span />
          </LoadingDots>
          <LoadingProgress />
          <div style={{ fontSize: "0.75rem", color: "#9c7b5a", marginTop: 16 }}>
            명리학 기반으로 정밀 분석 중...
          </div>
        </LoadingOverlay>
      </>
    );
  }

  /* ─── 미리보기 + 결제 화면 ─── */
  if (preview) {
    const svc = SERVICE_TYPES.find(s => s.key === preview.serviceType);
    const color = svc?.color || "#C8901E";
    const unlockItems = SVC_UNLOCK[preview.serviceType] || [];
    const price = SVC_PRICE[preview.serviceType] || 9900;

    /* 무료발송 모드 (PAYMENT_ENABLED=false) */
    if (!preview.paymentEnabled) {
      return (
        <>
          <Global />
          <PreviewPage>
            <PreviewTopBar />
            <PreviewHero>
              <PreviewCharImg src="/woo_y_1.png" alt="우연" />
              <PreviewBrandBox>
                <PreviewBrand>우연 사주</PreviewBrand>
                <PreviewBrandSub>분석이 완료되었습니다</PreviewBrandSub>
              </PreviewBrandBox>
            </PreviewHero>
            <PreviewBadge $color={color}>{preview.serviceType}</PreviewBadge>
            <PreviewCard>
              <PreviewCardTop />
              <PreviewCardInner>
                <PreviewAnalysisTitle>{form.name}님의 분석 결과</PreviewAnalysisTitle>
                <div style={{ fontSize: "0.88rem", color: "#3d2b1f", lineHeight: 1.9, paddingBottom: 20 }}>
                  {preview.text}
                </div>
              </PreviewCardInner>
              <PreviewLockSection>
                {preview.emailSent && form.email ? (
                  <div style={{ padding: "12px 0", background: "#ebfbee", borderRadius: 14, marginBottom: 14 }}>
                    <div style={{ fontSize: "1.4rem", marginBottom: 6 }}>✅</div>
                    <div style={{ fontSize: "0.9rem", fontWeight: 800, color: "#2f9e44" }}>
                      <strong>{form.email}</strong>으로 리포트가 발송되었습니다!
                    </div>
                  </div>
                ) : null}
                <PayBtn
                  onClick={() => { window.location.href = `/report/${preview.consultationId}`; }}
                  style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
                >
                  📖 전체 리포트 바로 보기
                </PayBtn>
              </PreviewLockSection>
            </PreviewCard>
            <div style={{ padding: "16px 20px 0", width: "100%", maxWidth: 520 }}>
              <ResetBtn onClick={() => {
                setPreview(null); setStep(0);
                setForm({ name: "", email: "", gender: "female", birth_date: "", birth_time: "", birth_time_unknown: false, birth_type: "solar", city: "서울", consent: false });
                setPartner(defaultPartner); setOptions([]); setError("");
              }}>← 다시 입력하기</ResetBtn>
            </div>
          </PreviewPage>
        </>
      );
    }

    /* 결제 필요 모드 (PAYMENT_ENABLED=true) */
    return (
      <>
        <Global />
        <PreviewPage>
          <PreviewTopBar />

          <PreviewHero>
            <PreviewCharImg src="/woo_y_1.png" alt="우연" />
            <PreviewBrandBox>
              <PreviewBrand>우연 사주</PreviewBrand>
              <PreviewBrandSub>정밀 명리학 운세 분석</PreviewBrandSub>
            </PreviewBrandBox>
          </PreviewHero>

          <PreviewBadge $color={color}>{preview.serviceType} 분석 완료</PreviewBadge>

          {/* 미리보기 카드 */}
          <PreviewCard style={{ position: "relative" }}>
            <SparkleWrap>
              {[
                { x:12, y:15, d:0,   s:0.7 },
                { x:80, y:10, d:0.8, s:0.6 },
                { x:90, y:55, d:1.5, s:0.5 },
                { x:5,  y:70, d:2.2, s:0.6 },
                { x:55, y:5,  d:1.1, s:0.5 },
              ].map((sp, i) => (
                <SparkleItem key={i} $x={sp.x} $y={sp.y} $delay={sp.d} $size={sp.s}>✦</SparkleItem>
              ))}
            </SparkleWrap>
            <PreviewCardTop />
            <PreviewCardInner>
              <PreviewAnalysisTitle>
                {form.name}님의 운명 일부가 보입니다
              </PreviewAnalysisTitle>
              <PreviewTextBoxOuter>
                <PreviewScrollArea>
                  <PreviewTextBox>
                    {preview.text}
                  </PreviewTextBox>
                </PreviewScrollArea>
                <PreviewFade />
              </PreviewTextBoxOuter>
            </PreviewCardInner>

            <PreviewLockSection>
              <LockIcon><img src="/woo_y_4.png" alt="" style={{ width: 48, height: 48, objectFit: "contain", filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.15))" }} /></LockIcon>
              <LockTitle>나머지 운명은 베일에 가려져 있습니다</LockTitle>
              <LockSub>
                결제 후 전체 내용을 이메일로 즉시 받아보세요.<br />
                아래 내용이 모두 포함됩니다.
              </LockSub>
              <PayBtn onClick={handlePayment}>
                ₩{price.toLocaleString()} — 전체 운명 확인하기
              </PayBtn>
              <PayInfo>
                결제 즉시 <strong>{form.email || "입력한 이메일"}</strong>로 발송 · 7일간 유효<br />
                <a href="/legal/refund" target="_blank" rel="noreferrer"
                  style={{ color: "#C8901E", textDecoration: "underline" }}>
                  환불정책
                </a> &nbsp;·&nbsp; 열람 전 7일 이내 전액 환불 가능
              </PayInfo>
            </PreviewLockSection>
          </PreviewCard>

          <div style={{ padding: "16px 20px 0", width: "100%", maxWidth: 520 }}>
            <ResetBtn onClick={() => {
              setPreview(null); setStep(0);
              setForm({ name: "", email: "", gender: "female", birth_date: "", birth_time: "", birth_time_unknown: false, birth_type: "solar", city: "서울", consent: false });
              setPartner(defaultPartner); setOptions([]); setError("");
            }}>← 다시 입력하기</ResetBtn>
          </div>
        </PreviewPage>
      </>
    );
  }

  /* ─── STEP 0: 서비스 선택(훅) 페이지 ─── */
  if (step === 0) {
    return (
      <>
        <Global />
        <DancheongBar />
        <Wrap>
          {/* 전체 씬 이미지 히어로 */}
          <HeroSceneWrap>
            <HeroSceneImg src="/woo_01.png" alt="우연 사주" />
            <HeroSceneOverlay />
            <HeroTitleBlock>
              <HeroTitle>우연 사주</HeroTitle>
              <HeroSub>정밀 명리학 운세 분석</HeroSub>
            </HeroTitleBlock>
          </HeroSceneWrap>

          <HookWrap>
            <SectionLabel>어떤 운세를 알고 싶으신가요?</SectionLabel>
            <ServiceCardGrid>
              {SERVICE_TYPES.map((s, idx) => (
                <ServiceCard
                  key={s.key}
                  type="button"
                  $color={s.color}
                  $idx={idx}
                  onClick={() => selectService(s.key)}
                >
                  <SvcCardImg $src={s.img} />
                  <SvcCardBody $color={s.color}>
                    <SvcIconImg src={s.iconImg} alt={s.key} />
                    <SvcHook $color={s.color}>{s.hook}</SvcHook>
                    <SvcSub>{s.sub}</SvcSub>
                    <SvcBtn $color={s.color}>{s.key} 보기 →</SvcBtn>
                  </SvcCardBody>
                </ServiceCard>
              ))}
            </ServiceCardGrid>
          </HookWrap>
          <TestEmailBox />
        </Wrap>
      </>
    );
  }

  /* ─── STEP 1: 입력 폼 페이지 ─── */
  const svc = SERVICE_TYPES.find(s => s.key === serviceType)!;

  return (
    <>
      <Global />
      <DancheongBar />
      {/* ── 시주 추정 설문 모달 ─────────────────────────── */}
      {surveying && (
        <SurveyOverlay onClick={e => { if (e.target === e.currentTarget) closeSurvey(); }}>
          <SurveyModal>
            <div style={{fontSize:"1rem",fontWeight:800,color:"#3d2b1f",marginBottom:4}}>
              출생시간 추정 설문
            </div>
            <div style={{fontSize:"0.78rem",color:"#9a7d6a",marginBottom:18}}>
              10개 문항으로 가능성 높은 시주를 추천해드립니다.<br/>
              {surveying === "partner" ? "상대방의 특성에 맞게 답해주세요." : ""}
            </div>

            {surveyResults.length === 0 ? (
              <>
                {surveyQs.map((q, qi) => (
                  <SurveyQBlock key={q.id}>
                    <SurveyQText>{qi + 1}. {q.question}</SurveyQText>
                    {q.choices.map((c, ci) => (
                      <SurveyChoiceLabel key={ci} $selected={surveyAns[qi] === ci}
                        onClick={() => setSurveyAns(a => { const n=[...a]; n[qi]=ci; return n; })}>
                        <input type="radio" readOnly checked={surveyAns[qi] === ci}
                          style={{marginTop:2,accentColor:"#c09a6b",flexShrink:0}} />
                        {c}
                      </SurveyChoiceLabel>
                    ))}
                  </SurveyQBlock>
                ))}
                <div style={{display:"flex",gap:10,marginTop:8}}>
                  <button onClick={closeSurvey}
                    style={{flex:1,padding:"10px",border:"1px solid #d4c0aa",borderRadius:10,background:"#f5f0ea",cursor:"pointer",fontSize:"0.82rem",color:"#7a6654"}}>
                    취소
                  </button>
                  <button onClick={submitSurvey}
                    disabled={surveyAns.some(a=>a<0)}
                    style={{flex:2,padding:"10px",border:"none",borderRadius:10,background:surveyAns.some(a=>a<0)?"#d4c0aa":"#c09a6b",color:"#fff",cursor:surveyAns.some(a=>a<0)?"not-allowed":"pointer",fontSize:"0.82rem",fontWeight:700}}>
                    {surveyAns.some(a=>a<0) ? `${surveyAns.filter(a=>a<0).length}개 질문 남음` : "시주 추정하기"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{fontSize:"0.83rem",fontWeight:700,color:"#3d2b1f",marginBottom:10}}>추정 결과 (가능성 높은 순)</div>
                {surveyResults.map((r) => (
                  <SurveyResultItem key={r.rank} $rank={r.rank}>
                    <div style={{width:26,height:26,borderRadius:"50%",background:r.rank===1?"#F59F00":r.rank===2?"#9775FA":"#74C0FC",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.75rem",fontWeight:800,flexShrink:0}}>{r.rank}</div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:800,fontSize:"0.88rem",color:"#2b1d10"}}>{r.name} ({r.hanja}) — {r.time_range}</div>
                      <div style={{fontSize:"0.75rem",color:"#888",marginTop:1}}>오행: {r.ohaeng} · {r.animal}띠 · 일치도 {r.confidence}%</div>
                    </div>
                    <button onClick={() => applySurveyResult(r.birth_hour)}
                      style={{padding:"6px 12px",border:"none",borderRadius:8,background:"#c09a6b",color:"#fff",fontSize:"0.75rem",fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                      이 시간으로 설정
                    </button>
                  </SurveyResultItem>
                ))}
                <div style={{display:"flex",gap:10,marginTop:10}}>
                  <button onClick={() => setSurveyResults([])}
                    style={{flex:1,padding:"9px",border:"1px solid #d4c0aa",borderRadius:10,background:"#f5f0ea",cursor:"pointer",fontSize:"0.8rem",color:"#7a6654"}}>
                    다시 풀기
                  </button>
                  <button onClick={closeSurvey}
                    style={{flex:1,padding:"9px",border:"1px solid #d4c0aa",borderRadius:10,background:"#f5f0ea",cursor:"pointer",fontSize:"0.8rem",color:"#7a6654"}}>
                    닫기
                  </button>
                </div>
              </>
            )}
          </SurveyModal>
        </SurveyOverlay>
      )}
      <Wrap>
        {/* Step 1 전체 씬 히어로 — 서비스별 이미지 사용 */}
        <HeroSceneWrap>
          <HeroSceneImg src={svc.img || "/woo_04.png"} alt={svc.key} />
          <HeroSceneOverlay />
          <HeroTitleBlock>
            <HeroTitle>{svc.icon} {svc.key}</HeroTitle>
            <HeroSub>{svc.sub}</HeroSub>
          </HeroTitleBlock>
        </HeroSceneWrap>

        <FormWrap style={{ padding: "0 14px" }}>
          <BackBtn type="button" onClick={() => { setStep(0); setError(""); }}>
            ← 운세 선택으로 돌아가기
          </BackBtn>

          <Card>
            {/* 서비스 훅 */}
            <div style={{
              background: `${svc.color}10`, borderRadius: 12, padding: "12px 14px",
              marginBottom: 16, borderLeft: `3px solid ${svc.color}`,
            }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 800, color: svc.color, marginBottom: 2 }}>{svc.hook}</div>
              <div style={{ fontSize: "0.72rem", color: "#7a6654" }}>{svc.sub}</div>
            </div>

            {/* 관계운 필수 안내 */}
            {isCompat && (
              <CompatNoticeBand>
                <CompatNoticeTitle>⚠️ 상대방 정보 필수 입력</CompatNoticeTitle>
                <CompatNoticeText>
                  당신만 준비된다고 되는 것이 아닙니다.<br />
                  상대의 마음이 열리는 <strong>'문턱'</strong>을 계산하기 위해 상대방 정보가 반드시 필요합니다.
                </CompatNoticeText>
              </CompatNoticeBand>
            )}

            {/* 개별운세 서브옵션 */}
            <SubOptWrap $visible={serviceType === "개별운세"}>
              <SubOptInner>
                <SubOptLabel>
                  세부 항목 선택 (복수 선택 가능)
                  {isMinor && age !== null && (
                    <span style={{ marginLeft: 8, color: "#E03131" }}>
                      · 미성년({age}세) — 항목 자동 전환
                    </span>
                  )}
                </SubOptLabel>
                <ChipRow>
                  {currentOptions.map(opt => (
                    <Chip key={opt} type="button" $active={options.includes(opt)}
                      onClick={() => toggleOption(opt)}>
                      {opt}
                    </Chip>
                  ))}
                </ChipRow>
              </SubOptInner>
            </SubOptWrap>

            <FormBody onSubmit={handleSubmit}>
              <SectionTitle>기본 정보</SectionTitle>
              <FieldRow>
                <Field>
                  <Label>이름 *</Label>
                  <Input placeholder="홍길동" value={form.name} onChange={set("name")} inputMode="text" autoComplete="name" />
                </Field>
                <Field>
                  <Label>성별 *</Label>
                  <Select value={form.gender} onChange={set("gender")}>
                    <option value="female">여성</option>
                    <option value="male">남성</option>
                  </Select>
                </Field>
              </FieldRow>
              <Field style={{ marginBottom: 10 }}>
                <Label>이메일 * — 결과 리포트가 이메일로 발송됩니다</Label>
                <Input type="email" placeholder="example@email.com"
                  value={form.email} onChange={set("email")}
                  inputMode="email" autoComplete="email" required />
              </Field>

              <SectionTitle>생년월일시</SectionTitle>
              <FieldRow>
                <Field>
                  <Label>구분</Label>
                  <Select value={form.birth_type} onChange={set("birth_type")}>
                    {BIRTH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Select>
                </Field>
                <Field style={{ flex: 2 }}>
                  <Label>생년월일 * (예: 1990/05/15)</Label>
                  <Input placeholder="1990/05/15" maxLength={10}
                    value={form.birth_date} onChange={set("birth_date")} />
                </Field>
              </FieldRow>
              <FieldRow style={{ alignItems: "center", marginBottom: 4 }}>
                <Label style={{ marginBottom: 0 }}>태어난 시간</Label>
                <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#7a6654", cursor: "pointer", marginLeft: 8 }}>
                  <input type="checkbox" checked={form.birth_time_unknown}
                    onChange={e => setForm(f => ({ ...f, birth_time_unknown: e.target.checked }))}
                    style={{ accentColor: "#c09a6b" }} />
                  시간 모름
                </label>
                {form.birth_time_unknown && (
                  <button type="button" onClick={() => openSurvey("self")}
                    style={{marginLeft:"auto",padding:"4px 10px",border:"1px solid #c09a6b",borderRadius:6,background:"#fff8f0",color:"#c09a6b",fontSize:"0.73rem",fontWeight:700,cursor:"pointer"}}>
                    ✨ 시간 추정 도움받기
                  </button>
                )}
              </FieldRow>
              <FieldRow>
                <Field>
                  <Input placeholder="예: 14:30 또는 1430" maxLength={5}
                    value={form.birth_time_unknown ? "" : form.birth_time}
                    disabled={form.birth_time_unknown}
                    style={form.birth_time_unknown ? { background: "#f5f0ea", color: "#b0a090", cursor: "not-allowed" } : {}}
                    onChange={set("birth_time")} />
                </Field>
              </FieldRow>

              <SectionTitle>지역 (시간 보정용)</SectionTitle>
              <Field style={{ marginBottom: 0 }}>
                <CityAutocomplete
                  value={form.city}
                  onChange={v => setForm(f => ({ ...f, city: v }))}
                />
              </Field>

              {/* 관계운 파트너 폼 */}
              <PartnerWrap $visible={isCompat}>
                <PartnerDivider>
                  <PartnerTitle>
                    💕 상대방 정보
                  </PartnerTitle>
                  <PartnerSub>
                    {serviceType === "연애궁합"
                      ? "연애궁합: 첫 만남 년도·속궁합·성격궁합·한난조습까지 분석합니다"
                      : "심층궁합: 결혼운·자녀운·이별위험시기까지 정밀 분석합니다"}
                  </PartnerSub>
                  <FieldRow>
                    <Field>
                      <Label>상대방 이름 *</Label>
                      <Input placeholder="김영희" value={partner.name} onChange={setP("name")} />
                    </Field>
                    <Field>
                      <Label>성별 *</Label>
                      <Select value={partner.gender} onChange={setP("gender")}>
                        <option value="female">여성</option>
                        <option value="male">남성</option>
                      </Select>
                    </Field>
                  </FieldRow>
                  <FieldRow>
                    <Field>
                      <Label>구분</Label>
                      <Select value={partner.birth_type} onChange={setP("birth_type")}>
                        {BIRTH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </Select>
                    </Field>
                    <Field style={{ flex: 2 }}>
                      <Label>생년월일 * (예: 1990/05/15)</Label>
                      <Input placeholder="1990/05/15" maxLength={10}
                        value={partner.birth_date} onChange={setP("birth_date")} />
                    </Field>
                  </FieldRow>
                  <FieldRow style={{ alignItems: "center", marginBottom: 4 }}>
                    <Label style={{ marginBottom: 0 }}>태어난 시간</Label>
                    <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#7a6654", cursor: "pointer", marginLeft: 8 }}>
                      <input type="checkbox" checked={partner.birth_time_unknown}
                        onChange={e => setPartner(p => ({ ...p, birth_time_unknown: e.target.checked }))}
                        style={{ accentColor: "#c09a6b" }} />
                      시간 모름
                    </label>
                    {partner.birth_time_unknown && (
                      <button type="button" onClick={() => openSurvey("partner")}
                        style={{marginLeft:"auto",padding:"4px 10px",border:"1px solid #c09a6b",borderRadius:6,background:"#fff8f0",color:"#c09a6b",fontSize:"0.73rem",fontWeight:700,cursor:"pointer"}}>
                        ✨ 시간 추정 도움받기
                      </button>
                    )}
                  </FieldRow>
                  <FieldRow>
                    <Field>
                      <Input placeholder="예: 14:30 또는 1430" maxLength={5}
                        value={partner.birth_time_unknown ? "" : partner.birth_time}
                        disabled={partner.birth_time_unknown}
                        style={partner.birth_time_unknown ? { background: "#f5f0ea", color: "#b0a090", cursor: "not-allowed" } : {}}
                        onChange={setP("birth_time")} />
                    </Field>
                  </FieldRow>
                  <Field style={{ marginBottom: 4 }}>
                    <Label>지역</Label>
                    <CityAutocomplete
                      value={partner.city}
                      onChange={v => setPartner(p => ({ ...p, city: v }))}
                    />
                  </Field>
                </PartnerDivider>
              </PartnerWrap>

              <ConsentBox>
                <input type="checkbox" checked={form.consent}
                  onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))} />
                <span>
                  <a href="/legal/terms" target="_blank" rel="noreferrer" style={{ color: "#9c7b5a" }}>이용약관</a>
                  {" "}및{" "}
                  <a href="/legal/privacy" target="_blank" rel="noreferrer" style={{ color: "#9c7b5a" }}>개인정보 처리방침</a>
                  에 동의하며, 개인정보(이름, 생년월일)를 사주 분석 목적으로 수집·이용하는 것에 동의합니다. (필수)
                </span>
              </ConsentBox>
            </FormBody>

            {error && <ErrorMsg>{error}</ErrorMsg>}

            <SubmitBtn
              type="submit"
              $disabled={loading}
              onClick={handleSubmit as any}
              disabled={loading}
            >
              {loading ? "접수 중..." : `${svc.icon} ${svc.key} 접수하기`}
            </SubmitBtn>
          </Card>
        </FormWrap>

        {/* ── 사업자 정보 푸터 ── */}
        <SiteFooter />

        {/* ── 테스트용 이메일 발송 버튼 ── */}
        <TestEmailBox />
      </Wrap>
    </>
  );
}

/* ── 테스트 이메일 컴포넌트 ── */
function TestEmailBox() {
  const [open, setOpen]     = React.useState(false);
  const [email, setEmail]   = React.useState("wooyeonsaju@gmail.com");
  const [name, setName]     = React.useState("테스트");
  const [svc, setSvc]       = React.useState("종합운세");
  const [cid, setCid]       = React.useState("1");
  const [msg, setMsg]       = React.useState("");
  const [sending, setSending] = React.useState(false);
  const API = process.env.REACT_APP_API_URL || "";

  const send = async () => {
    setSending(true); setMsg("");
    try {
      await axios.post(`${API}/api/v1/client/test-email`, {
        to_email: email, name, service_type: svc,
        consultation_id: Number(cid) || 0,
      });
      setMsg("✅ 발송 성공! 받은 편지함을 확인하세요.");
    } catch (e: any) {
      setMsg(`❌ 실패: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ maxWidth: 520, margin: "16px auto 0", textAlign: "center" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: "none", border: "1.5px dashed #c09a6b",
          borderRadius: 10, padding: "8px 18px",
          fontSize: "0.75rem", color: "#9c7b5a",
          cursor: "pointer", fontFamily: "inherit",
        }}
      >
        🧪 테스트 이메일 발송
      </button>

      {open && (
        <div style={{
          marginTop: 12, padding: "18px 16px",
          background: "#fff", border: "1.5px solid #e0d5c8",
          borderRadius: 14, textAlign: "left",
          boxShadow: "0 2px 12px rgba(100,70,40,0.08)",
        }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#3d2b1f", marginBottom: 12 }}>
            테스트 이메일 — 결제 없이 바로 발송
          </div>
          {[
            { label: "받는 이메일", val: email, set: setEmail, ph: "이메일 주소" },
            { label: "이름",       val: name,  set: setName,  ph: "홍길동" },
            { label: "consultation_id", val: cid, set: setCid, ph: "1" },
          ].map(({ label, val, set, ph }) => (
            <div key={label} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: "0.7rem", color: "#9c7b5a", marginBottom: 2 }}>{label}</div>
              <input
                value={val} onChange={e => set(e.target.value)}
                placeholder={ph}
                style={{
                  width: "100%", padding: "8px 10px", borderRadius: 8,
                  border: "1.5px solid #e0d5c8", fontSize: "0.82rem",
                  fontFamily: "inherit", color: "#3d2b1f", outline: "none",
                }}
              />
            </div>
          ))}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: "0.7rem", color: "#9c7b5a", marginBottom: 2 }}>서비스 타입</div>
            <select
              value={svc} onChange={e => setSvc(e.target.value)}
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 8,
                border: "1.5px solid #e0d5c8", fontSize: "0.82rem",
                fontFamily: "inherit", color: "#3d2b1f", background: "#fff",
              }}
            >
              {["종합운세","신년운세","연애운","연애궁합","심층궁합","직업운","사업(재물운)","건강운"].map(s => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <button
            onClick={send} disabled={sending}
            style={{
              width: "100%", padding: "10px", borderRadius: 10,
              background: sending ? "#e0d5c8" : "linear-gradient(135deg,#c09a6b,#a07850)",
              color: "#fff", border: "none", fontSize: "0.85rem",
              fontWeight: 700, cursor: sending ? "default" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {sending ? "발송 중..." : "📨 테스트 메일 보내기"}
          </button>
          {msg && (
            <div style={{
              marginTop: 10, fontSize: "0.78rem", color: msg.startsWith("✅") ? "#2F9E44" : "#E03131",
              textAlign: "center", fontWeight: 700,
            }}>
              {msg}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
